import moment from 'moment'

import {LoadSpacingConfiguration} from '../../../fields/loadSpacing/types'
import {FieldNames, FieldsForm} from '../../../fields/types'
import {findFieldName} from '../../../fields/utils/fieldNames.utils'
import {phoneNumberToString} from '../../../utils/phoneNumberUtils'
import {formatDurationISO8601} from '../../../utils/timeUtils'
import {OrderDto} from '../../types'

import {ContactInformationRequestDto, OrderChangeRequestPayload} from './types'

/**
 * Options for mapping order form to order change request payload
 */
export interface MapOrderFormToOrderChangeRequestOptions {
  /** Current order data */
  order: OrderDto
  /** List of field names that have been modified */
  changedFields: FieldNames[]
  loadSpacingConfiguration?: LoadSpacingConfiguration['loadSpacing']
}

/**
 * Build contact information from form data
 *
 * @param formData - Current form data
 * @param changedFields - List of changed field names
 * @returns Contact information or null if no contact fields changed
 */
const buildContactInformation = (
  formData: FieldsForm,
  changedFields: FieldNames[]
): ContactInformationRequestDto | null => {
  if (!findFieldName(changedFields, 'siteContact')) {
    return null
  }
  return {
    siteContactName: formData.siteContact?.name || undefined,
    siteContactNumber: phoneNumberToString(formData.siteContact?.phoneNumber) || undefined
  }
}

/**
 * Map quantity fields to payload
 * Uses discriminated union: quantity.type determines whether to send quantity or fullLoads.
 *
 * @param formData - Current form data
 * @param changedFields - List of changed field names
 * @param payload - Payload object to update
 */
const mapQuantityFields = (
  formData: FieldsForm,
  changedFields: FieldNames[],
  payload: OrderChangeRequestPayload
): void => {
  const quantityChanged = findFieldName(changedFields, 'quantity')

  if (quantityChanged) {
    if (formData.quantity.type === 'fullLoads') {
      payload.quantity = {type: 'fullLoads', value: formData.quantity.fullLoads}
    } else {
      payload.quantity = {type: 'volume', value: formData.quantity.volume}
    }
  }

  if (findFieldName(changedFields, 'plusLoadExpected')) {
    payload.quantityChangeExpected = formData.plusLoadExpected ?? null
  }
}

/**
 * Map material and contract fields to payload
 *
 * @param formData - Current form data
 * @param changedFields - List of changed field names
 * @param payload - Payload object to update
 */
const mapMaterialAndContractFields = (
  formData: FieldsForm,
  changedFields: FieldNames[],
  payload: OrderChangeRequestPayload
): void => {
  if (findFieldName(changedFields, 'material')) {
    payload.materialNumber = formData.material?.materialNumber ?? null
  }

  if (findFieldName(changedFields, 'contract')) {
    payload.contractNumber = formData.contract?.contractNumber ?? null
    payload.contractItem = formData.contract?.contractItemPositionNumber ?? null
  }
}

/**
 * Map time-related fields (load spacing, flow rate, time on job site) to payload
 *
 * @param formData - Current form data
 * @param changedFields - List of changed field names
 * @param payload - Payload object to update
 */
const mapTimeRelatedFields = (
  formData: FieldsForm,
  changedFields: FieldNames[],
  payload: OrderChangeRequestPayload,
  configuration?: LoadSpacingConfiguration['loadSpacing']
): void => {
  if (findFieldName(changedFields, 'spacingOrFlowRate')) {
    if (formData.spacingOrFlowRate) {
      // loadSpace and flowRate are mutually exclusive representations of the same
      // interval. When one is chosen the other must be explicitly cleared, otherwise
      // the order keeps both modes set and the backend rejects the change.
      if (formData.spacingOrFlowRate.type === 'loadSpace') {
        payload.loadSpace = formatDurationISO8601(formData.spacingOrFlowRate.loadSpace)
        payload.flowRate = null
      } else {
        payload.flowRate = formData.spacingOrFlowRate.flowRate ?? null
        payload.loadSpace = null
      }
    } else {
      // Field was cleared (e.g. auto-adjustment set it to undefined for single-truck)
      payload.flowRate = null
      payload.loadSpace = null
    }
  } else if (findFieldName(changedFields, 'quantity') && !formData.isMultipleTruck) {
    // When quantity switches to single-truck but spacingOrFlowRate was not touched
    // (auto-adjustment disabled for non-Pending/Reserved/Confirmed statuses),
    // explicitly clear both so the backend does not reject the stale values.
    payload.flowRate = null
    payload.loadSpace = null
  }

  const orderChange = configuration?.isEnabled ? configuration.orderChange : undefined
  if (orderChange) {
    for (const field of orderChange.disabledFields) {
      if (!formData.isMultipleTruck) {
        delete payload[field]
      } else if (!orderChange.originalIsMultipleTruck) {
        payload[field] = null
      }
    }
  }

  if (findFieldName(changedFields, 'timeOnJobSite') && formData.timeOnJobSite) {
    payload.timeOnJobSite = formatDurationISO8601(formData.timeOnJobSite.time)
  }
}

/**
 * Map material variation fields to payload
 *
 * @param formData - Current form data
 * @param changedFields - List of changed field names
 * @param payload - Payload object to update
 */
const mapMaterialVariationFields = (
  formData: FieldsForm,
  changedFields: FieldNames[],
  payload: OrderChangeRequestPayload
): void => {
  if (findFieldName(changedFields, 'materialVariations') && formData.materialVariations?.slump) {
    payload.slump = formData.materialVariations.slump.value ?? null
  }

  if (
    findFieldName(changedFields, 'materialVariations') &&
    formData.materialVariations?.setControl
  ) {
    payload.setControl = formData.materialVariations.setControl.value ?? null
  }
}

/**
 * Maps order change form values to order change request API payload.
 *
 * This function transforms form data into the structure expected by the
 * PATCH /ecs/order-management/change/{orderId} endpoint as defined in OpenAPI specification.
 * Only changed fields are included in the payload (based on changedFields list).
 *
 * @param formData - Order form values from react-hook-form
 * @param options - Additional context (order, changedFields)
 * @returns API payload ready to send to order change endpoint (OrderChangeRequestDto)
 *
 * @see {@link https://api.heidelbergmaterials.com/ecs/order-management OpenAPI Specification}
 */
export const mapOrderFormToOrderChangeRequest = (
  formData: FieldsForm,
  options: MapOrderFormToOrderChangeRequestOptions
): OrderChangeRequestPayload => {
  const {order, changedFields} = options

  // Always include checkSum for optimistic locking
  const payload: OrderChangeRequestPayload = {
    checkSum: order.checkSum ?? ''
  }

  // Confirmation (customer confirmation)
  payload.confirmationPin = formData.confirmationPin || null

  if (findFieldName(changedFields, 'isConfirmedOrder')) {
    payload.isConfirmedByCustomer = formData.isConfirmedOrder
  }

  // Shipping date
  if (findFieldName(changedFields, 'dateTime') && formData.dateTime?.[0]) {
    payload.shippingDate = moment(formData.dateTime[0]).toISOString()
  }

  // Quantity
  mapQuantityFields(formData, changedFields, payload)

  // Material and contract
  mapMaterialAndContractFields(formData, changedFields, payload)

  // Contact information (site contact only per OpenAPI spec)
  const contactInfo = buildContactInformation(formData, changedFields)
  if (contactInfo) {
    payload.contactInformation = contactInfo
  }

  // Time-related fields (load spacing, flow rate, time on job site)
  mapTimeRelatedFields(formData, changedFields, payload, options.loadSpacingConfiguration)

  // Customer reference (PO number)
  if (findFieldName(changedFields, 'poNumber')) {
    payload.customerReference = formData.poNumber || null
  }

  // Test booking
  if (findFieldName(changedFields, 'isTestBooked')) {
    payload.isTestBooked = formData.isTestBooked ?? null
  }

  // Order change notes (not orderCreateNotes per OpenAPI spec)
  if (findFieldName(changedFields, 'additionalNotes')) {
    payload.orderChangeNotes = formData.additionalNotes || null
  }

  // Material variations
  mapMaterialVariationFields(formData, changedFields, payload)

  // Balance load
  if (findFieldName(changedFields, 'maxLoadSize')) {
    payload.maxLoadSize = formData.maxLoadSize ?? null
  }

  return payload
}
